import FrontEndApiService from '@services/frontend.api.service';
import {invitationPropertyAddress, propertyAddress} from "@helpers/order";
import {availableThirdPartyPlatformList, getActiveThirdPartyPlatformList, setupEngine} from "@helpers/api-helper";
import {pageRedirect} from "@helpers/router-helper";
import {useAuthStore} from '@store';

export const redirectToZipformLogin = async (externalId, redirectUrl) => {
    const {ZIPFORM_IFRAME_BASE_URL, ZIPFORM_SHARED_KEY} = window.env;

    await pageRedirect(
        ZIPFORM_IFRAME_BASE_URL,
        `/corews/ssologin.aspx?appKey=${encodeURIComponent(ZIPFORM_SHARED_KEY)}&openVw=true&extId=${encodeURIComponent(externalId)}&onSuccessURL=${encodeURIComponent(redirectUrl)}`,
        false
    );
};

export const redirectToDotLoopLogin = async (externalId, redirectUrl) => {
    const storeAuth = useAuthStore();
    const {DOTLOOP_IFRAME_BASE_URL, DOTLOOP_EXTERNAL_ID} = window.env;

    //DotLoop validated only the domain name - on top of that only production for now (any uri suffix or query string disrupts the process)
    await storeAuth.setThirdPartyInternRedirect(redirectUrl);
    //successURL = window.env.APP_URL;
    redirectUrl = 'https://protect3.sellersshield.com';

    await pageRedirect(
        DOTLOOP_IFRAME_BASE_URL,
        `/oauth/authorize?response_type=code&client_id=${encodeURIComponent(DOTLOOP_EXTERNAL_ID)}&redirect_uri=${encodeURIComponent(redirectUrl)}`,
        false
    );
};

export const thirdPartyService = {
    getActiveThirdPartyPlatformList: getActiveThirdPartyPlatformList,

    async redirectToWebhook() {
        const storeAuth = useAuthStore();
        //in some cases like dotloop we get the auth code on domain root instead of our webhook
        let urlTo = storeAuth.thirdPartyInternRedirect,
            regexList = urlTo ? (await availableThirdPartyPlatformList()).join('|') : 'testABC',
            regexReferrer = new RegExp(`[a-zA-Z].(?:${regexList})`, 'g'),
            params = new URLSearchParams(window.location.search),
            doRedirect = urlTo && regexReferrer.test(urlTo) && (window.location.href || '').indexOf('webhook') < 10;

        params = [
            params.get('token') ? 'token=' + params.get('token') : null,
            params.get('code') ? 'code=' + params.get('code') : null,
        ].filter(p => !!p).join('&');

        if (doRedirect && params) {
            await storeAuth.setThirdPartyInternRedirect('');
            await pageRedirect(urlTo + (urlTo.indexOf('?') ? '&' : '?'), params, false);
            return true;
        }
        return false;
    },

    async isPlatformAvailable(platform) {
        return (await availableThirdPartyPlatformList(platform) || []).length > 0;
    },

    getRedirectPage($key) {
        return {
            inviteSeller: 'invite-seller',
            orderDetails: 'order-details',
            'invite-seller': 'invite-seller',
            'order-details': 'order-details',
        }[$key] || null;
    },
    getPropertyAddressFromOrderResource(orderAppResource, states = []) {
        return propertyAddress(orderAppResource) || invitationPropertyAddress(orderAppResource, states);
    },

    getPlatformRecordAddress(selected, fallbackRecordName = '', platform = 'zipform') {
        if (selected) {
            let address, zip, city, state;

            switch (platform) {
                case 'zipform':
                    [address, zip, city] = (selected.name || fallbackRecordName).split(',');

                    return [
                        selected.propertyAddress || address,
                        selected.zip || zip,
                        selected.city || city,
                    ];

                case 'dotloop':

                    if (selected.name) {
                        [address, city, state] = selected.name.split(',');
                    } else {
                        [address, zip, city] = fallbackRecordName.split(',');
                    }

                    let propertyAddress = selected.details ? (selected.details['Property Address'] || {}) : {};
                    let addressTmp = [
                        propertyAddress['Street Number'] || selected.streetNumber,
                        propertyAddress['Street Name'] || selected.streetName
                    ].filter(e => !!e).join(' ');

                    Boolean(addressTmp) && (address = addressTmp);
                    Boolean(propertyAddress['City']) && (city = propertyAddress['City']);
                    Boolean(propertyAddress['Zip/Postal Code']) && (zip = propertyAddress['Zip/Postal Code']);

                    return [
                        address,
                        selected.zipCode || zip,
                        selected.city || city,
                    ];
            }
        }

        return [];
    },
    getPlatformRecordSeller(record) {
        let selected = (record?.participants || []).find(person => person?.role === 'SELLER') || record;
        return {
            fullName: selected?.seller || selected?.ownerName || selected?.fullName,
            ...selected
        }
    },

    redirectToLogin(externalId, redirectUrl, orderUuid = null, platform = 'zipform') {
        try {
            if (!thirdPartyService.isPlatformAvailable(platform)) {
                throw `Platform ${platform} is unavailable or disabled!`;
            }

            let successURL = `${window.env.APP_URL}/${platform}/webhook?page=${redirectUrl}&token=${externalId}`;
            Boolean(orderUuid) && (successURL += `&orderUuid=${orderUuid}`);

            switch (platform) {
                case 'zipform':
                    return redirectToZipformLogin(externalId, successURL);
                case 'dotloop':
                    return redirectToDotLoopLogin(externalId, successURL);
            }
        } catch (error) {
            console.log(error);
            return false;
        }
        return true;
    },

    async doRequest(endpoint, requestBody) {
        try {
            FrontEndApiService.init(window.env.MILESTONES_API_HOST);
            let response = await FrontEndApiService.post(endpoint, requestBody);

            if (response?.data) {
                return response.data;
            }

            return response;
        } catch (error) {
            throw error;
        }
    },

    async getRecords(contextId, status, platform = 'zipform') {
        if (contextId && status && thirdPartyService.isPlatformAvailable(platform)) {

            let config = setupEngine();
            let response = await thirdPartyService.doRequest(`${config.apiPrefix}/${config.apiVersion}/${platform}/records`, {
                contextId,
                status,
            });

            if (Array.isArray(response)) {
                return response;
            }
        }

        return [];
    },

    async createRecord(contextId, propertyAddress, platform = 'zipform', orderUuid=null, integrationType='referring_platform') {
        if (!contextId || !propertyAddress || !thirdPartyService.isPlatformAvailable(platform)) {
            return null;
        }
        let config = setupEngine();
        return await thirdPartyService.doRequest(`${config.apiPrefix}/${config.apiVersion}/${platform}/records/create`, {
            contextId,
            propertyAddress,
            orderUuid,
            integrationType
        });
    },
    async createRecordFromOrderResource(contextId, orderAppResource, states = [], platform = 'zipform',  integrationType='referring_platform') {
        return await thirdPartyService.createRecord(contextId, thirdPartyService.getPropertyAddressFromOrderResource(orderAppResource, states), platform, orderAppResource.uuid, integrationType);
    },

    async sendFiles(contextId, externalId, disclosureUuid, orderUuid, platform = 'zipform') {
        let data = null;

        if (contextId && externalId && thirdPartyService.isPlatformAvailable(platform)) {
            let config = setupEngine();
            data = await thirdPartyService.doRequest(`${config.apiPrefix}/${config.apiVersion}/${platform}/records/send-files`, {
                contextId,
                externalId,
                disclosureUuid,
                orderUuid,
            });
        }

        if (data) {
            return data;
        }

        throw new Error(`Could not send files to ${platform}!`);
    }
};